import { z } from "zod";
import { SCOPE_TYPES } from "./permissions";

/**
 * Password policy (OWASP ASVS L2): length over complexity rules.
 * Max length bounds hashing cost (DoS protection).
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, { message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.` })
  .max(PASSWORD_MAX_LENGTH, { message: `Password must be at most ${PASSWORD_MAX_LENGTH} characters.` });

/** Normalise first (pasted emails often carry spaces or capitals), then validate. */
export const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(320));

export const loginRequestSchema = z.object({
  email: emailSchema,
  // Login does not apply the policy: an existing password must still be accepted.
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

/** One-time token from an invite or reset email: 32 random bytes, base64url. */
export const oneTimeTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, { message: "Invalid token." });

export const acceptInviteRequestSchema = z.object({
  token: oneTimeTokenSchema,
  password: passwordSchema,
});
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequestSchema>;

export const passwordResetRequestSchema = z.object({ email: emailSchema });
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

export const passwordResetConfirmSchema = z.object({
  token: oneTimeTokenSchema,
  password: passwordSchema,
});
export type PasswordResetConfirm = z.infer<typeof passwordResetConfirmSchema>;

/** Sign-in methods offered on the login page (ADR-0028). `entra`: Microsoft sign-in is configured. */
export const authProvidersSchema = z.object({ entra: z.boolean() });
export type AuthProviders = z.infer<typeof authProvidersSchema>;

/**
 * Why a Microsoft sign-in did not complete: the callback redirects to `/login?sso=<code>`.
 * Deliberately coarse — the audit log records the detail.
 */
export const SSO_ERRORS = ["cancelled", "not_allowed", "account_disabled", "account_conflict", "failed"] as const;
export type SsoError = (typeof SSO_ERRORS)[number];

/** Where a sign-in may return to: an in-app path only (never another origin or `//host`). */
export function safeReturnPath(value: unknown): string {
  return typeof value === "string" && /^\/(?![/\\])[^\s]*$/.test(value) && value.length <= 512 ? value : "/";
}

export const roleAssignmentSchema = z.object({
  roleKey: z.string(),
  scopeType: z.enum(SCOPE_TYPES),
  scopeId: z.uuid().nullable(),
});
export type RoleAssignment = z.infer<typeof roleAssignmentSchema>;

export const currentUserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  displayName: z.string(),
  locale: z.enum(["it", "en"]),
  roles: z.array(roleAssignmentSchema),
  permissions: z.array(z.string()),
});
export type CurrentUser = z.infer<typeof currentUserSchema>;

/** PUT /api/auth/me/locale — the language the user picked in the header; emails follow it. */
export const updateLocaleRequestSchema = z.object({ locale: z.enum(["it", "en"]) });
export type UpdateLocaleRequest = z.infer<typeof updateLocaleRequestSchema>;
